import { action, computed, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { Id } from '@/infra/id'
import type { NavigateFn } from '@/RootStore'
import { PrefilledDraftInput } from '@/features/jobs/drafting/PrefilledDraftInput'
import { type JobCreationRootViewModel } from '@/features/jobs/drafting/JobCreationRootViewModel'
import { type DraftJobViewModel } from '@/features/jobs/drafting/DraftJobViewModel'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import { type Member } from '@/features/iam/members/Member'
import { Job } from '@/features/jobs/Job'
import { links } from '@/utils/link'
import { CreateJobConfirmationDialogViewModel } from '@/features/call-center/CreateJobConfirmationDialog/CreateJobConfirmationDialogViewModel'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { type JobCheckoutViewModel } from '@/features/jobs/drafting/checkout/JobCheckoutViewModel'
import type {
  ApplyJobCouponByCodeFn,
  JobCheckoutViewModelFactory,
  RemoveJobCouponByCodeFn,
  SubmitJobRequestFn,
} from '@/features/jobs'
import { tapAction, toStream } from '@/utils/mobx-util'
import { withAbortSignal } from '@/utils/rx-util'
import { EMPTY } from 'rxjs'
import {
  catchError,
  debounceTime,
  filter,
  switchMap,
  tap,
} from 'rxjs/operators'
import { AddCouponDialogViewModel } from '@/features/call-center/AddCouponDialog/AddCouponDialogViewModel'
import type {
  EmailCheckoutLinkFn,
  GenerateCheckoutLinkFn,
  InvalidateCheckoutSessionsFn,
} from '@/features/billing'
import { CallCenterCheckoutViewModel } from '@/features/call-center/JobCheckout/CallCenterCheckoutViewModel'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { Membership } from '@/features/iam/workspaces/Membership'

export class CallCenterJobViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The editor whose legend is picked automatically.
   */
  @observable.ref
  private accessor _legendAutoSelectionEditor: DraftJobViewModel | null = null

  @observable
  accessor activeWorkspace: Workspace | null = null

  @observable
  accessor activeMember: Member | null = null

  @observable
  accessor requiredQuestionsAmount = 0

  readonly confirmationDialog: CreateJobConfirmationDialogViewModel

  /**
   * Checkout for the open job, replaced whenever another job is opened.
   */
  @observable.ref
  accessor jobCheckoutVm: JobCheckoutViewModel

  readonly addCouponDialogVm: AddCouponDialogViewModel

  readonly checkoutVm: CallCenterCheckoutViewModel

  constructor(
    private readonly _jobCreationRoot: JobCreationRootViewModel,
    private readonly _createJobCheckoutViewModel: JobCheckoutViewModelFactory,
    private readonly _submitJobRequest: SubmitJobRequestFn,
    applyJobCouponByCode: ApplyJobCouponByCodeFn,
    private readonly _removeJobCouponByCode: RemoveJobCouponByCodeFn,
    private readonly _generateCheckoutLink: GenerateCheckoutLinkFn,
    private readonly _invalidateCheckoutSessions: InvalidateCheckoutSessionsFn,
    private readonly _emailCheckoutLink: EmailCheckoutLinkFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _navigate: NavigateFn,
  ) {
    this.confirmationDialog = new CreateJobConfirmationDialogViewModel(
      (membership) => this.initializeJob(membership),
    )

    this.jobCheckoutVm = _createJobCheckoutViewModel(Id.none)

    this.checkoutVm = new CallCenterCheckoutViewModel(() => this.jobCheckoutVm)

    this.addCouponDialogVm = new AddCouponDialogViewModel(
      () => this.jobCheckoutVm.job,
      applyJobCouponByCode,
      _newAlert,
      _createAlertErrorHandler,
      () => this._invalidateCheckoutSessions(this.jobCheckoutVm.job.id, true),
    )

    this._lifecycle
      .manage(
        this._jobCreationRoot.jobCreated$.pipe(
          tap((job) => {
            if (job === Job.none) {
              return
            }

            this._openCheckout(job.id)
            const memberName = this.activeMember?.displayName ?? ''
            const workspaceName = this.activeWorkspace?.name || 'workspace'

            this._newAlert()
              .withState('success')
              .saying(
                'Job created',
                `Draft job created for ${memberName} in ${workspaceName}.`,
              )
              .autoDismiss()
              .show()

            // Navigate to the new job URL
            this._navigate(links.callCenterJob(job.id))
          }),
        ),
      )
      .manage(
        toStream(() => this._requiredQuestions).pipe(
          filter(() => this.jobCheckoutVm.job.id !== Id.none),
          debounceTime(300),
          switchMap(() =>
            withAbortSignal((signal) =>
              Promise.all([
                this.jobCheckoutVm.fetchJobInfo(signal),
                this._invalidateCheckoutSessions(
                  this.jobCheckoutVm.job.id,
                  false,
                ),
              ]).catch(
                this._createAlertErrorHandler('Refreshing the checkout failed'),
              ),
            ).pipe(
              // The alert has reported the failure.
              catchError(() => EMPTY),
            ),
          ),
        ),
      )
      .manage(
        toStream(() => this._legendAutoSelectionEditor, true).pipe(
          switchMap((editor) =>
            editor
              ? toStream(
                  () => editor.legendSelector.legends.map((l) => l.id),
                  true,
                ).pipe(tapAction(() => this._autoSelectLegend(editor)))
              : EMPTY,
          ),
        ),
      )
  }

  @computed
  get hasActiveClient() {
    return Boolean(this.activeMember && this.activeWorkspace)
  }

  @computed
  get jobCreation(): DraftJobViewModel | null {
    if (!this.hasActiveClient) {
      return null
    }

    return this._jobCreationRoot.current
  }

  @computed
  private get _requiredQuestions() {
    const jobCreation = this.jobCreation
    if (!jobCreation) {
      return undefined
    }

    return (jobCreation.intakeQuestions.questionList?.questionVms ?? []).slice(
      0,
      this.requiredQuestionsAmount,
    )
  }

  @computed
  get legendSteps() {
    return this.jobCreation?.legendSelector.stepAccessors ?? []
  }

  @computed
  private get _questionSteps() {
    return this.jobCreation?.intakeQuestions.stepAccessors ?? []
  }

  @computed
  get requiredQuestionSteps() {
    return this._questionSteps.slice(0, this._requiredQuestionStepCount)
  }

  @computed
  get optionalQuestionSteps() {
    return this._questionSteps.slice(this._requiredQuestionStepCount)
  }

  @computed
  get taxTypeLegends() {
    return (
      this.jobCreation?.legendSelector.legends.filter(
        (legend) => legend.serviceType === 'Tax',
      ) ?? []
    )
  }

  @computed
  get isFormEmpty() {
    return this._questionSteps.length === 0 && this.legendSteps.length === 0
  }

  @computed
  get isReady() {
    return this._requiredQuestions?.every(
      (question) => question.editor.isAnswered,
    )
  }

  @computed
  get isCheckoutReady() {
    if (this.requiredQuestionsAmount === 0) {
      return true
    }

    return (
      this._requiredQuestions?.every(
        (question) => question.editor.isAnswered,
      ) ?? false
    )
  }

  @computed
  get isEmailingCheckoutLink() {
    return this.emailCheckoutLink.pending
  }

  @computed
  get canSubmitJob() {
    return (
      this.activeWorkspace?.config.billing.enabled &&
      this.jobCheckoutVm.publishedLegend.skipBilling
    )
  }

  @computed
  get jobScopeItems() {
    const job = this.jobCheckoutVm.job

    return this.jobCheckoutVm.job.jobScopeItems.filter(
      (item) => item.id !== job.jobBundle?.id,
    )
  }

  @action.bound
  resetActiveJobState() {
    this.activeWorkspace = null
    this.activeMember = null
    this.requiredQuestionsAmount = 0

    this._legendAutoSelectionEditor = null
  }

  @action.bound
  goToCustomerLookup() {
    this.resetActiveJobState()
    this._navigate(links.callCenter())
  }

  @action.bound
  goBackToJob() {
    this._navigate(links.callCenterJob(this.jobCheckoutVm.id))
  }

  initializeJob = task.resolved(async (membership: Membership) => {
    const workspaceId = Id.assert(membership.workspace.id)
    const ownerPublicId = Id.assert(membership.member.user.publicId)

    const prefilled = new PrefilledDraftInput(
      undefined,
      ownerPublicId,
      null,
      [],
      {
        publicId: ownerPublicId,
        workspaceIds: [workspaceId],
      },
    )

    const editor = await this._jobCreationRoot
      .edit(Id.none, prefilled)
      .catch(this._createAlertErrorHandler('Starting the job failed'))

    runInAction(() => {
      editor.workspaceSelector.selectWorkspace(membership.workspace)
      editor.ownerSelector.selectOwner(membership.member)

      this.activeWorkspace = membership.workspace
      this.activeMember = membership.member
      this.requiredQuestionsAmount = this._getRequiredQuestionAmount(
        membership.workspace,
      )
      this._legendAutoSelectionEditor = editor
    })

    this.confirmationDialog.modal.close()
  })

  openExistingJob = task.resolved(async (job: Job, signal?: AbortSignal) => {
    this._openCheckout(job.id)
    await this.jobCheckoutVm.fetchJobInfo(signal)
    signal?.throwIfAborted()

    // Load the job into the job creation editor.
    const editor = await this._jobCreationRoot.edit(
      Id.assert(job.id),
      PrefilledDraftInput.empty(),
    )

    runInAction(() => {
      this.activeWorkspace = job.workspace
      this.activeMember = job.owner || null
      this.requiredQuestionsAmount = this._getRequiredQuestionAmount(
        job.workspace,
      )
      this._legendAutoSelectionEditor = editor
    })
  })

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this.resetActiveJobState()
    this._lifecycle.deactivate()
    this._jobCreationRoot.deactivate()
  }

  removeCoupon = task.resolved(async () => {
    const jobId = this.jobCheckoutVm.job.id
    await this._invalidateCheckoutSessions(jobId, true)
      .then(() => this._removeJobCouponByCode(jobId))
      .then(() => this.jobCheckoutVm.fetchQuote(this._lifecycle.signal))
      .catch(this._createAlertErrorHandler('Removing the coupon failed'))

    this._newAlert()
      .withState('success')
      .saying('Coupon removed', `Coupon removed.`)
      .show()
  })

  /**
   * Allow to submit the job when `skipBilling` is enabled.
   */
  submitJob = task.resolved(async () => {
    const job = this.jobCheckoutVm.job

    await this._submitJobRequest({ id: job.id }).catch(
      this._createAlertErrorHandler('Submitting the job failed'),
    )

    this._newAlert()
      .withState('success')
      .saying('Job submitted', 'The job has been submitted successfully.')
      .autoDismiss()
      .show()

    this.goToCustomerLookup()
  })

  @action.bound
  copyCheckoutLink() {
    const job = this.jobCheckoutVm.job
    // Fire-and-forget: triggers Stripe checkout session creation,
    // which updates HubSpot "quote presented" via external integration.
    this._generateCheckoutLink(job.id).catch(() => null)
  }

  emailCheckoutLink = task.resolved(async () => {
    const job = this.jobCheckoutVm.job
    const userPublicId = job.championClient?.member.user.publicId

    if (!userPublicId) {
      return
    }

    const errorHandler = this._createAlertErrorHandler(
      'Emailing the checkout link failed',
    )
    // It's not necessary to generate a link before sending an email.
    // Generating a link now creates a Stripe checkout session now, which is
    // used for analytics. Generating the checkout link now is solely for analytics.
    // The user will only receive an email containing the stable link.
    await this._generateCheckoutLink(job.id).catch(errorHandler)
    await this._emailCheckoutLink({
      workspaceId: job.workspace.id,
      userId: userPublicId,
      jobId: job.id,
    }).catch(errorHandler)

    this._newAlert()
      .withState('success')
      .saying('Checkout link email sent')
      .autoDismiss()
      .show()
  })

  @action.bound
  private _openCheckout(jobId: Id) {
    if (this.jobCheckoutVm.id !== jobId) {
      this.jobCheckoutVm = this._createJobCheckoutViewModel(jobId)
    }
  }

  @computed
  private get _requiredQuestionStepCount() {
    return Math.max(
      0,
      Math.min(this.requiredQuestionsAmount, this._questionSteps.length),
    )
  }

  private _getRequiredQuestionAmount(workspace: Workspace) {
    return workspace.config.callCenter.totalRequiredQuestions
  }

  private _autoSelectLegend(editor: DraftJobViewModel) {
    const selected = editor.legendSelector.selectedLegendVersion
    const taxTypeLegends = editor.legendSelector.legends.filter(
      (l) => l.serviceType === 'Tax',
    )
    if (selected && selected.serviceType !== 'Tax') {
      editor.legendSelector.selectLegend(null)
    }

    if (editor.legendSelector.selectedLegendVersion) {
      return
    }

    if (taxTypeLegends.length === 1) {
      editor.legendSelector.selectLegend(taxTypeLegends[0], true)
    }
  }
}
